<script setup lang="ts">
import type { RouteLocationRaw } from 'vue-router'
import AppFeedDetails from '@/components/feed/AppFeedDetails.vue'
import FeedTopBar from '@/components/feed/FeedTopBar.vue'
import ResumeView from '@/components/feed/ResumeView.vue'
import VacancyView from '@/components/feed/VacancyView.vue'
import ListingCompanyVacancies from '@/components/listing/ListingCompanyVacancies.vue'
import { Button } from '@/components/ui/button'
import type { FeedActionKind } from '@/composables/feed/use-feed-actions'
import { toListingCardModel, type ListingPreviewData } from '@/composables/listing/use-listing-preview'
import { formatShortDate } from '@/composables/new-ui'

// A vacancy or resume as two full-screen items with the feed's vertical snap:
// the feed card with its video, then the details that scroll on their own.
// It is not a feed: there is nothing to rank or page. The video holds while
// the details are on screen and resumes when the viewer swipes back.

type Section = 'video' | 'details'

const props = withDefaults(defineProps<{
  data: ListingPreviewData
  responseId?: string | null
  favoriteActive?: boolean
  hasResponse?: boolean
  canAct?: boolean
  primaryLabel?: string | null
  isOwner?: boolean
  /** On screen: the video plays only while the screen is visible. */
  visible: boolean
  /** No bottom navigation underneath (a guest, a sheet). */
  sheet?: boolean
  /** In a sheet: a close button instead of «Назад». */
  closable?: boolean
  /** Where another vacancy of the company opens. */
  linkTo: (id: string) => RouteLocationRaw
}>(), {
  responseId: null,
  favoriteActive: false,
  hasResponse: false,
  canAct: true,
  primaryLabel: null,
  isOwner: false,
  sheet: false,
  closable: false,
})

const emit = defineEmits<{
  action: [kind: FeedActionKind]
  close: []
  back: []
}>()

const scroller = useTemplateRef<HTMLElement>('scroller')
const section = ref<Section>('video')
const feedDetailsOpen = useState<boolean>('feedDetailsDrawerOpen', () => false)

const item = computed(() => props.data.item)
const card = computed(() => toListingCardModel(item.value))
const hasVideos = computed(() => item.value.videos.length > 0)
const ownerLinks = computed(() => {
  if (!props.isOwner) return []
  if (item.value.type === 'summary') return [{ label: 'Редактировать', to: `/applicant/summaries/edit/${item.value.id}` }]
  const base = `/company/${item.value.vacancy.company.id}/vacancies/${item.value.id}`
  return [{ label: 'Редактировать', to: `${base}/edit` }, { label: 'Отклики', to: `${base}/responses` }]
})
const dateLabel = computed(() => {
  if (!props.data.date) return null
  return `${item.value.type === 'vacancy' ? 'Опубликовано' : 'Обновлено'} ${formatShortDate(props.data.date)}`
})

function onScroll() {
  const element = scroller.value
  if (element === null) return
  section.value = element.scrollTop > element.clientHeight / 2 ? 'details' : 'video'
}

function show(next: Section) {
  const element = scroller.value
  if (element === null) return
  const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches
  element.scrollTo({ top: next === 'details' ? element.clientHeight : 0, behavior: smooth ? 'smooth' : 'auto' })
}

defineExpose({ showDetails: () => show('details') })

// Like the feed's details, the second item covers the bottom navigation.
watchEffect(() => {
  if (!props.sheet) feedDetailsOpen.value = props.visible && section.value === 'details'
})
onBeforeUnmount(() => {
  if (!props.sheet) feedDetailsOpen.value = false
})
</script>

<template>
  <div class="relative size-full overflow-hidden bg-black" data-testid="listing-screen" :data-section="section">
    <div
      ref="scroller"
      class="size-full snap-y snap-mandatory overflow-y-auto overscroll-y-contain [&::-webkit-scrollbar]:hidden [scrollbar-width:none]"
      @scroll.passive="onScroll"
    >
      <section class="relative h-full w-full snap-start snap-always" aria-label="Видео">
        <ResumeView
          v-if="item.type === 'summary'"
          :item="item"
          :active="visible && hasVideos"
          :suspended="section === 'details'"
          :favorite-active="favoriteActive"
          :response-context="!!responseId"
          :can-act="canAct"
          :sheet="sheet"
          class="size-full"
          @action="emit('action', $event)"
        />
        <VacancyView
          v-else
          :item="item"
          :active="visible && hasVideos"
          :suspended="section === 'details'"
          :favorite-active="favoriteActive"
          :has-response="hasResponse"
          :response-context="!!responseId"
          :can-act="canAct"
          :sheet="sheet"
          class="size-full"
          @action="emit('action', $event)"
        />
        <p
          v-if="data.notice"
          class="pointer-events-none absolute left-6 top-[4.75rem] z-30 max-w-[calc(100%-3rem)] truncate rounded-full bg-black/60 px-3 py-1.5 text-xs text-white backdrop-blur-sm"
        >
          {{ data.notice }}
        </p>
        <div
          v-if="!hasVideos"
          class="absolute inset-x-6 top-1/3 z-[25] flex flex-col items-center gap-3 text-center text-white"
        >
          <Icon name="tabler:video-off" class="size-10 text-white/70" aria-hidden="true" />
          <p class="text-base">Видео пока нет</p>
          <Button variant="secondary" shape="pill" size="sm" @click="show('details')">Смотреть детали</Button>
        </div>
      </section>

      <section
        class="relative flex h-full w-full snap-start snap-always flex-col bg-black pt-[4.75rem]"
        aria-label="Детали"
        data-vaul-no-drag
      >
        <AppFeedDetails
          :card="card"
          drawer
          :favorite-active="favoriteActive"
          :primary-label="primaryLabel"
          :primary-disabled="hasResponse && !responseId"
          class="!rounded-t-2xl !rounded-b-none !overscroll-y-auto"
          @action="emit('action', $event)"
        >
          <template #context>
            <div v-if="isOwner || data.notice" class="flex flex-col gap-3 border-b border-border p-4">
              <p v-if="isOwner" class="text-sm font-medium">{{ item.type === 'vacancy' ? 'Ваша вакансия' : 'Ваше резюме' }}</p>
              <p v-if="data.notice" class="flex items-center gap-2 text-sm text-muted-foreground" role="status">
                <Icon name="tabler:info-circle" class="size-4 shrink-0" aria-hidden="true" />{{ data.notice }}
              </p>
              <div v-if="ownerLinks.length" class="flex flex-wrap gap-2">
                <Button v-for="link in ownerLinks" :key="link.label" as-child variant="outline" size="sm" shape="pill">
                  <NuxtLink :to="link.to">{{ link.label }}</NuxtLink>
                </Button>
              </div>
            </div>
            <slot name="context" />
          </template>
          <template #after>
            <ListingCompanyVacancies
              v-if="visible && item.type === 'vacancy' && item.vacancy.company.id"
              :company-id="item.vacancy.company.id"
              :vacancy-id="item.id"
              :link-to="linkTo"
            />
            <p v-if="dateLabel" class="px-6 pb-6 text-sm text-muted-foreground">{{ dateLabel }}</p>
          </template>
        </AppFeedDetails>
      </section>
    </div>

    <FeedTopBar :tab="section" :closable="closable" @update:tab="show" @close="emit('close')">
      <template v-if="!closable" #leading>
        <Button
          type="button"
          variant="ghost"
          size="icon-lg"
          class="size-10 rounded-full border border-white/25 bg-black/30 text-white backdrop-blur-sm hover:bg-black/40 hover:text-white"
          aria-label="Назад"
          @click="emit('back')"
        >
          <Icon name="tabler:arrow-left" class="size-6" aria-hidden="true" />
        </Button>
      </template>
    </FeedTopBar>
  </div>
</template>
